import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  Button,
  CalendarDateRange,
  CalendarPicker,
  Dropdown,
  DropdownItem,
  SearchableDropdown,
  SearchableDropdownItem,
  Select,
  SelectItem,
} from "@fronterahealth/frontera-ui-components";

import {
  MediaSortByEnum,
  VideoSessionStatusEnums,
  useGetProvidersForSessionQuery,
  useGetSessionCaregiversQuery,
} from "@api/graphql/types-and-hooks";
import { useAllSessionClients } from "@hooks/useAllSessionClients";
import { useBreakpoint } from "@pages/VideoSessionsV2/hooks/useMediaMatcher";
import { useAdminData } from "@providers/AdminDataProvider";
import { defaultSortBy, useVideoSessionData } from "@providers/VideoSessionProviderV2";

const UPLOAD_STATUS: SearchableDropdownItem[] = [
  {
    id: VideoSessionStatusEnums.Uploading,
    label: "Pending Upload",
  },
  {
    id: JSON.stringify([VideoSessionStatusEnums.Scheduled, VideoSessionStatusEnums.Processing]),
    label: "Processing",
  },
  {
    id: VideoSessionStatusEnums.Completed,
    label: "Completed",
  },
  {
    id: VideoSessionStatusEnums.Failed,
    label: "Failed",
  },
];

export const FilterBar: React.FC = () => {
  const { pathname } = useLocation();
  const {
    setFilterSelectedClients,
    setFilterSelectedProviders,
    setFilterSelectedCaregivers,
    filterSelectedClients,
    filterSelectedProviders,
    filterSelectedCaregivers,
    filterSessionDates,
    setFilterSessionDates,
    filterSelectedRating,
    setFilterSelectedRating,
    sortBy,
    setSortBy,
    isFilterActive,
    clearFeedFilters,
    filterByUploadStatus,
    setFilterByUploadStatus,
  } = useVideoSessionData();
  const { isUserCaregiver, isUserRBT } = useAdminData();
  const { clients: sessionClients, isLoading: isSessionClientsDataLoading } = useAllSessionClients(
    pathname.includes("/video-sessions-v2"),
  );

  const [sessionProviders, setSessionProviders] = useState<SearchableDropdownItem[]>([]);
  const [sessionCaregivers, setSessionCaregivers] = useState<SearchableDropdownItem[]>([]);

  const [recentSearchesForClient, setRecentSearchesForClient] = useState<SearchableDropdownItem[]>([]);
  const [recentSearchesForProvider, setRecentSearchesForProvider] = useState<SearchableDropdownItem[]>([]);
  const [recentSearchesForCaregiver, setRecentSearchesForCaregiver] = useState<SearchableDropdownItem[]>([]);

  const { isMobile, isDesktop, isTablet } = useBreakpoint();

  const canFetchSessionParticipants = pathname.includes("/video-sessions-v2") && !isUserRBT && !isUserCaregiver;

  const { data: sessionProvidersData, isFetching: isSessionProvidersDataLoading } = useGetProvidersForSessionQuery(
    {},
    {
      queryKey: ["getProvidersForSession"],
      refetchOnWindowFocus: false,
      retry: false,
      enabled: canFetchSessionParticipants,
    },
  );
  const { data: sessionCaregiversData, isFetching: isSessioncaregiversDataLoading } = useGetSessionCaregiversQuery(
    {},
    {
      queryKey: ["getSessioncaregivers"],
      refetchOnWindowFocus: false,
      retry: false,
      enabled: canFetchSessionParticipants,
    },
  );

  useEffect(() => {
    if (sessionCaregiversData) {
      setSessionCaregivers(
        sessionCaregiversData?.getSessionCaregivers?.edges?.map((item) => ({
          id: item?.node?.id || "",
          label: `${item?.node?.caregivermetadata?.firstName || ""} ${item?.node?.caregivermetadata?.lastName || ""}`,
        })) || [],
      );
    }
  }, [sessionCaregiversData]);

  useEffect(() => {
    if (sessionProvidersData) {
      setSessionProviders(
        sessionProvidersData?.getProvidersForSession?.edges?.map((item) => ({
          id: item?.node?.id || "",
          label: `${item?.node?.providermetadata?.firstName || ""} ${item?.node?.providermetadata?.lastName || ""}`,
          category: "RBT",
        })) || [],
      );
    }
  }, [sessionProvidersData]);

  const handleRecentSearchAddForClient = (item: SearchableDropdownItem) => {
    setRecentSearchesForClient((prev) => [...prev, item].slice(-5)); // Keep the last 5 searches
  };

  const handleRecentSearchAddForProvider = (item: SearchableDropdownItem) => {
    setRecentSearchesForProvider((prev) => [...prev, item].slice(-5)); // Keep the last 5 searches
  };

  const handleRecentSearchAddForCaregiver = (item: SearchableDropdownItem) => {
    setRecentSearchesForCaregiver((prev) => [...prev, item].slice(-5)); // Keep the last 5 searches
  };

  const handleDateChange = (newDates: CalendarDateRange) => {
    setFilterSessionDates(newDates);
  };
  const dropdownItems: DropdownItem[] = [
    {
      id: "clients",

      label: (
        <SearchableDropdown
          items={sessionClients}
          label="Client"
          placeholder="Search Client"
          onSelectionChange={(items: SearchableDropdownItem[]) => {
            setFilterSelectedClients(items);
          }}
          recentSearches={recentSearchesForClient}
          onRecentSearchAdd={handleRecentSearchAddForClient}
          isLoading={isSessionClientsDataLoading}
          selectedItems={filterSelectedClients}
          disabled={isSessionClientsDataLoading}
          variant="dropdown"
          containerClassNames="w-full"
        />
      ),
    },
    {
      id: "rbt",
      label: (
        <SearchableDropdown
          items={sessionProviders}
          label="RBT"
          placeholder="Search RBT"
          onSelectionChange={(items: SearchableDropdownItem[]) => {
            setFilterSelectedProviders(items);
          }}
          recentSearches={recentSearchesForProvider}
          onRecentSearchAdd={handleRecentSearchAddForProvider}
          isLoading={isSessionProvidersDataLoading}
          selectedItems={filterSelectedProviders}
          disabled={isSessionProvidersDataLoading}
          variant="dropdown"
          containerClassNames="w-full"
        />
      ),
    },
    {
      id: "caregiver",
      label: (
        <SearchableDropdown
          items={sessionCaregivers}
          label="Caregiver"
          placeholder="Search Caregiver"
          onSelectionChange={(items: SearchableDropdownItem[]) => {
            setFilterSelectedCaregivers(items);
          }}
          recentSearches={recentSearchesForCaregiver}
          onRecentSearchAdd={handleRecentSearchAddForCaregiver}
          isLoading={isSessioncaregiversDataLoading}
          selectedItems={filterSelectedCaregivers}
          disabled={isSessioncaregiversDataLoading}
          variant="dropdown"
          containerClassNames="w-full"
        />
      ),
    },
    {
      id: "sessionDate",
      label: (
        <CalendarPicker
          placeholder="Session Date"
          selectedDateRange={filterSessionDates}
          inputSize="md"
          onDateChange={handleDateChange}
          containerClass="max-w-56! min-w-56!"
          variant="dropdown"
        />
      ),
    },
    {
      id: "Rating",
      label: (
        <Select
          items={[
            { id: "1", primary: "★" },
            { id: "2", primary: "★ ★" },
            { id: "3", primary: "★ ★ ★" },
            { id: "4", primary: "★ ★ ★ ★" },
            { id: "5", primary: "★ ★ ★ ★ ★" },
          ]}
          title=""
          placeholderText="Session Rating"
          setSelected={(item: SelectItem) => {
            setFilterSelectedRating(item);
          }}
          selected={filterSelectedRating}
          className="flex"
          inputContainerClassName="mt-0 w-full"
          actionItem={{
            label: "Clear",
            onClick: () => {
              setFilterSelectedRating(undefined);
            },
          }}
          variant="dropdown"
        />
      ),
    },
    {
      id: "Upload Status",
      label: (
        <SearchableDropdown
          items={UPLOAD_STATUS}
          label="Upload Status"
          placeholder="Upload Status"
          onSelectionChange={(items: SearchableDropdownItem[]) => {
            setFilterByUploadStatus(items);
          }}
          selectedItems={filterByUploadStatus}
          variant="dropdown"
          containerClassNames="w-full"
        />
      ),
    },
  ];

  const responsiveItems =
    isMobile || isTablet
      ? dropdownItems
      : isTablet
        ? dropdownItems.filter((item) => item.id !== "clients")
        : dropdownItems.filter(
            (item) =>
              item.id !== "rbt" && item.id !== "sessionDate" && item.id !== "caregiver" && item.id !== "clients",
          );

  const dropDownFilteredItems =
    isUserRBT || isUserCaregiver
      ? isUserCaregiver
        ? responsiveItems.filter((item) => item.id !== "rbt" && item.id !== "caregiver" && item.id !== "Rating")
        : responsiveItems.filter((item) => item.id !== "rbt" && item.id !== "caregiver")
      : responsiveItems;

  return (
    <div
      className={`w-full max-md:gap-2 gap-y-2 pb-4 flex flex-row flex-wrap justify-between ${isMobile ? "items-top" : "items-center"} `}
    >
      <div className={`${isMobile ? "flex-col" : "flex-row"} flex`}>
        <div className={`gap-1 flex flex-wrap -mx-4 px-4 ${isMobile ? "items-top" : "items-center"}`}>
          {!isMobile ? (
            <SearchableDropdown
              items={sessionClients}
              label="Client"
              placeholder="Search Client"
              onSelectionChange={(items: SearchableDropdownItem[]) => {
                setFilterSelectedClients(items);
              }}
              recentSearches={recentSearchesForClient}
              onRecentSearchAdd={handleRecentSearchAddForClient}
              isLoading={isSessionClientsDataLoading}
              selectedItems={filterSelectedClients}
              disabled={isSessionClientsDataLoading}
            />
          ) : null}
          {!isUserRBT && !isUserCaregiver && isDesktop ? (
            <>
              <SearchableDropdown
                items={sessionProviders}
                label="RBT"
                placeholder="Search RBT"
                onSelectionChange={(items: SearchableDropdownItem[]) => {
                  setFilterSelectedProviders(items);
                }}
                recentSearches={recentSearchesForProvider}
                onRecentSearchAdd={handleRecentSearchAddForProvider}
                isLoading={isSessionProvidersDataLoading}
                selectedItems={filterSelectedProviders}
                disabled={isSessionProvidersDataLoading}
              />
              <SearchableDropdown
                items={sessionCaregivers}
                label="Caregiver"
                placeholder="Search Caregiver"
                onSelectionChange={(items: SearchableDropdownItem[]) => {
                  setFilterSelectedCaregivers(items);
                }}
                recentSearches={recentSearchesForCaregiver}
                onRecentSearchAdd={handleRecentSearchAddForCaregiver}
                isLoading={isSessioncaregiversDataLoading}
                selectedItems={filterSelectedCaregivers}
                disabled={isSessioncaregiversDataLoading}
              />
            </>
          ) : null}
          {isDesktop ? (
            <CalendarPicker
              placeholder="Session Date"
              selectedDateRange={filterSessionDates}
              inputSize="md"
              onDateChange={handleDateChange}
              mode="filter"
            />
          ) : null}
          <Dropdown
            label={`${isMobile ? "Filters" : "More"}`}
            items={dropDownFilteredItems}
            placement={isDesktop ? "left" : "right"}
          />
        </div>
        <Button
          text="Clear Filters"
          appearance="link"
          className={`text-interaction-primary px-2! no-underline hover:no-underline ${isFilterActive ? "pointer-events-auto opacity-100" : "hidden"}`}
          onClick={clearFeedFilters}
        />
      </div>
      <div className={`gap-1 flex flex-row sm:justify-between ${isMobile ? "items-top" : "items-center"}`}>
        {/* Removing for now to prevent UI distored */}
        {/* <Small>{totalFilteredMediaCount} Videos</Small> */}
        <Select
          items={[
            { id: MediaSortByEnum.DescSessionDate, primary: "Most Recent Session" },
            { id: MediaSortByEnum.DescSessionUploadDate, primary: "Newest Upload" },
            { id: MediaSortByEnum.DescSessionRating, primary: "Highest Session Rating" },
            { id: MediaSortByEnum.AscSessionRating, primary: "Lowest Session Rating" },
          ]}
          title=""
          placeholderText=""
          selected={sortBy}
          setSelected={(item) => {
            setSortBy(item);
          }}
          inputContainerClassName="mt-0 sm:w-52"
          className="flex"
          actionItem={{
            label: "Reset Sort",
            onClick: () => {
              setSortBy(defaultSortBy);
            },
          }}
        />
      </div>
    </div>
  );
};
